import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { PiArrowLeft, PiArrowSquareOut, PiLinkBreak, PiWarning } from 'react-icons/pi'
import { motion } from 'framer-motion'

import QuickActionsModal from '@/components/dashboard/QuickActionsModal/QuickActionsModal'
import Button from '@/components/common/Button/Button'

import { useDriveStore } from '@/store/useDriveStore'
import { useUserStore } from '@/store/useUserStore'

import type { DriveCopyStatus } from '@/types/expense'

import BottomSheet from './BottomSheet'
import GoogleDriveIcon from './GoogleDriveIcon'

const MY_DRIVE_URL = 'https://drive.google.com/drive/my-drive'

const driveUrl = (folderId?: string | null) =>
  folderId ? `https://drive.google.com/drive/folders/${folderId}` : MY_DRIVE_URL

export const DriveManagementSheet = ({ copyState }: { copyState?: DriveCopyStatus | null }) => {
  const { t } = useTranslation()

  const activeSheet = useDriveStore((state) => state.activeSheet)
  const closeSheet = useDriveStore((state) => state.closeSheet)
  const disconnect = useDriveStore((state) => state.disconnect)
  const connect = useDriveStore((state) => state.connect)
  const isConnecting = useDriveStore((state) => state.isConnecting)
  const connection = useUserStore((state) => state.user?.google_drive_connection)

  const [confirmingDisconnect, setConfirmingDisconnect] = useState(false)

  const rowClass = 'flex items-center gap-3 w-full py-3.5 text-left text-base text-zinc-900'

  const disconnectActions = [
    {
      icon: (
        <div className="w-10 h-10 flex items-center justify-center bg-red-300 rounded-full">
          <PiLinkBreak className="w-5 h-5 text-[#1D2233]" />
        </div>
      ),
      text: t('expenses.drive.disconnectConfirm.confirm'),
      subtext: t('expenses.drive.disconnectConfirm.body'),
      onClick: () => {
        setConfirmingDisconnect(false)
        disconnect()
      },
      className: 'rounded-t-3xl rounded-b-xl',
    },
    {
      icon: (
        <div className="w-10 h-10 flex items-center justify-center bg-slate-100 rounded-full">
          <PiArrowLeft className="w-5 h-5 text-[#1D2233]" />
        </div>
      ),
      text: t('expenses.drive.disconnectConfirm.cancel'),
      subtext: '',
      onClick: () => setConfirmingDisconnect(false),
      className: 'rounded-b-3xl rounded-t-xl',
    },
  ]

  return (
    <>
      <BottomSheet isOpen={activeSheet === 'manage' && !confirmingDisconnect} onClose={closeSheet}>
        <div className="flex items-center gap-2 mb-4">
          <GoogleDriveIcon className="w-6 h-6" />
          <h3 className="text-lg font-semibold text-zinc-900">
            {t('expenses.drive.sheet.title')}
          </h3>
        </div>

        {connection?.status === 'revoked' && (
          <div className="rounded-xl bg-amber-50 border border-amber-200 p-4 mb-4">
            <div className="flex items-start gap-2">
              <PiWarning className="w-5 h-5 text-amber-500 shrink-0 mt-0.5" />
              <p className="text-sm text-amber-800">{t('expenses.drive.attention.auth')}</p>
            </div>
            <Button variant="secondary" isLoading={isConnecting} onClick={connect} className="h-11 w-full mt-3">
              <motion.span initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
                {t('expenses.drive.attention.authCta')}
              </motion.span>
            </Button>
          </div>
        )}

        {connection?.status !== 'revoked' && copyState === 'failed' && (
          <div className="flex items-start gap-2 rounded-xl bg-amber-50 border border-amber-200 p-4 mb-4">
            <PiWarning className="w-5 h-5 text-amber-500 shrink-0 mt-0.5" />
            <p className="text-sm text-amber-800">{t('expenses.drive.attention.failures')}</p>
          </div>
        )}

        <p className="text-sm text-zinc-500">
          {t('expenses.drive.sheet.connected')}
        </p>

        <div className="divide-y divide-zinc-100 border-t border-zinc-100 mt-4">
          <button type="button" className={rowClass} onClick={() => window.open(driveUrl(connection?.root_folder_id), '_blank')}>
            <PiArrowSquareOut className="w-5 h-5 text-zinc-700" />
            {t('expenses.drive.sheet.openFolder')}
          </button>
          <button type="button" className={rowClass} onClick={() => setConfirmingDisconnect(true)}>
            <PiLinkBreak className="w-5 h-5 text-zinc-700" />
            {t('expenses.drive.sheet.disconnect')}
          </button>
        </div>

        <p className="text-xs text-zinc-400 mt-3">
          {t('expenses.drive.sheet.privacyFooter')}
        </p>
      </BottomSheet>

      <QuickActionsModal
        actions={disconnectActions}
        isOpen={confirmingDisconnect}
        setOpen={setConfirmingDisconnect}
      />
    </>
  )
}

export default DriveManagementSheet
